import { MotionError, compileMotion, type CompiledMotion } from '../animation/procedural/compiler';
import { trackValueAt } from '../animation/timeline';
import type { Binding, MotionPlanInput } from '../animation/procedural/spec';
import { createId } from '../lib/ids';
import { CHANNEL_LABELS, animationOf, checkFps, createAnimation, withAnimation, DEFAULT_FPS } from './animationOps';
import { MAX_ANIMATION_SECONDS, type Animation, type AnimationTrack, type Channel, type Keyframe, type Project } from './schemas';
import { sceneOf } from './sceneOps';

/**
 * Applying compiled procedural motion to a project (Phase 3C). Everything here is pure:
 * it returns a new Project (one validated change — one undo step in the editor) or throws
 * and changes nothing. Applied motion is ordinary Phase 3B tracks and keyframes; no recipe
 * or plan is stored, so the result stays playable and editable on its own.
 *
 * Conflict policies (generated keys never silently overwrite existing ones):
 *  - `replace-range`: on every object channel the plan writes, existing keyframes inside the
 *    generated frame range are replaced; keys outside it are kept. The motion before the
 *    range is unchanged (see `boundaryLeadIn`), and after the range the object moves on to
 *    the next retained key with the original curve's interpolation (`boundaryTail`);
 *  - `merge`: refused (atomically) when any generated range contains existing keyframes;
 *  - `append`: the whole plan is shifted to start after the last existing keyframe of its
 *    targets;
 *  - `new-animation`: the plan goes into a new animation in the same scene.
 * A plan that ends after the animation is refused unless `allowExtend` (user-approved)
 * lengthens the animation to fit (up to the 120 s limit).
 */
export type ConflictPolicy = 'replace-range' | 'merge' | 'append' | 'new-animation';

export const CONFLICT_POLICIES: Record<ConflictPolicy, { label: string; description: string }> = {
  'replace-range': { label: 'Replace frame range', description: 'Existing keys inside the generated range of each channel are replaced; keys outside it stay.' },
  merge: { label: 'Merge into free frames', description: 'Only adds keys where the channel has none in that range; refuses otherwise.' },
  append: { label: 'Append after existing motion', description: 'Starts the plan after the last existing keyframe of its targets.' },
  'new-animation': { label: 'Create a new animation', description: 'Puts the generated motion into a new animation in this scene.' },
};

export interface MotionConflict {
  objectId: string;
  objectName: string;
  channel: Channel;
  /** Generated range on this channel. */
  from: number;
  to: number;
  /** Existing keyframes inside that range (they would be replaced). */
  frames: number[];
}

export interface MotionRequest {
  plan: MotionPlanInput;
  binding: Binding;
  policy: ConflictPolicy;
  sceneId: string;
  /** Required unless the policy is `new-animation` (it then supplies the default fps). */
  animationId: string | null;
  newAnimation?: { name?: string; fps?: number; seconds?: number };
  allowExtend?: boolean;
  /** Id factory (deterministic in tests). */
  newId?: (prefix: string) => string;
}

export interface MotionResult {
  project: Project;
  animationId: string;
  /** The animation with the motion applied (also used, uncommitted, as the preview). */
  animation: Animation;
  compiled: CompiledMotion;
  conflicts: MotionConflict[];
  replacedKeys: number;
  /** New duration (frames) when the animation was extended. */
  extendedTo: number | null;
  createdAnimation: boolean;
  summary: string;
}

/** Existing keyframes that fall inside the generated ranges (same object and channel). */
export function planConflicts(project: Project, sceneId: string, tracks: readonly AnimationTrack[], compiled: CompiledMotion): MotionConflict[] {
  const scene = sceneOf(project, sceneId);
  const out: MotionConflict[] = [];
  for (const g of compiled.tracks) {
    const t = tracks.find((x) => x.objectId === g.objectId && x.channel === g.channel);
    const frames = t ? t.keyframes.filter((k) => k.frame >= g.from && k.frame <= g.to).map((k) => k.frame) : [];
    if (frames.length) out.push({ objectId: g.objectId, objectName: scene.objects.find((o) => o.id === g.objectId)?.name ?? g.objectId, channel: g.channel, from: g.from, to: g.to, frames });
  }
  return out;
}

export const describeConflict = (c: MotionConflict): string =>
  `${c.objectName} · ${CHANNEL_LABELS[c.channel]}: ${c.frames.length} existing key${c.frames.length === 1 ? '' : 's'} in frames ${c.from}–${c.to}`;

/**
 * Compiles a plan against the project and computes the animation it produces under the
 * chosen policy. Does not commit anything: `result.project` is the would-be project.
 * Throws MotionError/OpError (with an actionable message) when the plan cannot be applied.
 */
export function applyMotionPlan(project: Project, request: MotionRequest): MotionResult {
  const scene = sceneOf(project, request.sceneId);
  const newId = request.newId ?? createId;
  const base: Animation | null = request.animationId ? animationOf(project, request.animationId) : null;
  if (base && base.sceneId !== scene.id) throw new MotionError(`“${base.name}” belongs to another scene.`);
  if (!base && request.policy !== 'new-animation') throw new MotionError('Select an animation (or choose “Create a new animation”).');
  const context = { scene, characters: project.characters, binding: request.binding };

  // ---- A new animation sized to the plan.
  if (request.policy === 'new-animation') {
    const fps = checkFps(request.newAnimation?.fps ?? base?.fps ?? DEFAULT_FPS);
    const probe = compileMotion(request.plan, { ...context, timing: { fps, durationFrames: MAX_ANIMATION_SECONDS * fps }, existing: null });
    const seconds = request.newAnimation?.seconds ?? Math.max(1, Math.ceil((probe.requiredFrames / fps) * 2) / 2);
    const created = createAnimation(project, scene.id, { name: request.newAnimation?.name, seconds, fps, loop: base?.loop ?? 'loop' }, newId('anm'));
    const fresh = animationOf(created.project, created.id);
    if (probe.requiredFrames > fresh.durationFrames) throw new MotionError(`The plan needs ${probe.requiredFrames} frames, but the new animation has ${fresh.durationFrames}.`);
    const tracks = probe.tracks.map<AnimationTrack>((g) => ({ id: newId('trk'), objectId: g.objectId, channel: g.channel, keyframes: g.keyframes }));
    const animation = { ...fresh, tracks };
    const next = withAnimation(created.project, animation);
    return { project: next, animationId: created.id, animation, compiled: probe, conflicts: [], replacedKeys: 0, extendedTo: null, createdAnimation: true, summary: summarize(probe, animation, 0, null, true) };
  }

  const a = base!;
  const compile = (frameOffset: number) =>
    compileMotion(request.plan, { ...context, timing: { fps: a.fps, durationFrames: a.durationFrames }, existing: a.tracks, allowOverflow: !!request.allowExtend, frameOffset });
  let compiled = compile(0);
  if (request.policy === 'append') {
    const targets = new Set(compiled.tracks.map((t) => t.objectId));
    const last = Math.max(-1, ...a.tracks.filter((t) => targets.has(t.objectId)).flatMap((t) => t.keyframes.map((k) => k.frame)));
    const offset = last >= compiled.startFrame ? last + 1 - compiled.startFrame : 0;
    if (offset > 0) compiled = compile(offset);
  }
  let duration = a.durationFrames;
  let extendedTo: number | null = null;
  if (compiled.requiredFrames > a.durationFrames) {
    if (!request.allowExtend) throw new MotionError(`The plan ends at frame ${compiled.endFrame}, after the last frame (${a.durationFrames - 1}) of “${a.name}”.`);
    if (compiled.requiredFrames > MAX_ANIMATION_SECONDS * a.fps) throw new MotionError(`The plan needs ${(compiled.requiredFrames / a.fps).toFixed(1)} s; an animation can last at most ${MAX_ANIMATION_SECONDS} s.`);
    duration = extendedTo = compiled.requiredFrames;
  }
  const conflicts = planConflicts(project, scene.id, a.tracks, compiled);
  if (conflicts.length && request.policy !== 'replace-range') {
    const lines = conflicts.map(describeConflict);
    throw new MotionError(
      `${conflicts.length} channel${conflicts.length === 1 ? '' : 's'} already ha${conflicts.length === 1 ? 's' : 've'} keyframes where the plan would write (${lines[0]}${conflicts.length > 1 ? ', …' : ''}). Choose “Replace frame range”, “Append” or “Create a new animation”.`,
      lines,
    );
  }
  let replaced = 0;
  const tracks = a.tracks.map((t) => ({ ...t }));
  for (const g of compiled.tracks) {
    const i = tracks.findIndex((t) => t.objectId === g.objectId && t.channel === g.channel);
    if (i < 0) {
      tracks.push({ id: newId('trk'), objectId: g.objectId, channel: g.channel, keyframes: g.keyframes });
      continue;
    }
    const original = tracks[i]!;
    const kept = original.keyframes.filter((k) => k.frame < g.from || k.frame > g.to);
    replaced += original.keyframes.length - kept.length;
    tracks[i] = { ...original, keyframes: mergeKeys(boundaryLeadIn(original, kept, g.from), boundaryTail(original, g)) };
  }
  const animation: Animation = { ...a, durationFrames: duration, tracks };
  const next = withAnimation(project, animation);
  return { project: next, animationId: a.id, animation, compiled, conflicts, replacedKeys: replaced, extendedTo, createdAnimation: false, summary: summarize(compiled, animation, replaced, extendedTo, false) };
}

/** Easings whose shape changes when their segment is shortened (ease-in, u³, does not). */
const RESHAPED_WHEN_CUT = new Set(['ease-out', 'ease-in-out']);
/** At most this many keys bake a cut lead-in segment (every frame up to this length). */
const MAX_LEAD_IN_KEYS = 120;

/**
 * Keeps the motion *before* a generated range exactly as it was. Inserting the plan's
 * first key into an existing eased segment would shorten that segment and re-time its
 * easing over the shorter span, changing frames the plan never touches. When the cut
 * segment's easing is not scale-invariant (ease-out, ease-in-out), it is baked into linear
 * keys sampled from the original curve: every frame (exact) for up to 120 frames, evenly
 * spaced beyond that. Linear, step and ease-in segments keep their shape when cut.
 */
function boundaryLeadIn(original: AnimationTrack, kept: Keyframe[], from: number): Keyframe[] {
  const keys = original.keyframes;
  const bi = keys.findLastIndex((k) => k.frame < from);
  const before = keys[bi];
  const next = keys[bi + 1];
  if (!before || !next || next.frame <= from || !RESHAPED_WHEN_CUT.has(before.easing)) return kept;
  const span = from - before.frame - 1;
  const stride = Math.max(1, Math.ceil(span / MAX_LEAD_IN_KEYS));
  const baked: Keyframe[] = [];
  for (let f = before.frame + stride; f < from; f += stride) baked.push({ frame: f, value: Math.round(trackValueAt(original, f)! * 10000) / 10000, easing: 'linear' });
  return [...kept.map((k) => (k === before ? { ...k, easing: 'linear' as const } : k)), ...baked];
}

/**
 * The plan's last key also shapes the segment from the range's end to the next retained
 * key. That segment was described by the original curve, not by the template, so it keeps
 * the interpolation the original curve had at that point (visibility always steps).
 */
function boundaryTail(original: AnimationTrack, g: { channel: Channel; to: number; keyframes: Keyframe[] }): Keyframe[] {
  const keys = original.keyframes;
  if (g.channel === 'visible' || !keys.some((k) => k.frame > g.to)) return g.keyframes;
  const governing = keys.findLast((k) => k.frame <= g.to);
  if (!governing) return g.keyframes;
  return g.keyframes.map((k, i) => (i === g.keyframes.length - 1 ? { ...k, easing: governing.easing } : k));
}

function mergeKeys(kept: readonly Keyframe[], generated: readonly Keyframe[]): Keyframe[] {
  const byFrame = new Map(kept.map((k) => [k.frame, k]));
  for (const k of generated) byFrame.set(k.frame, k);
  return [...byFrame.values()].sort((p, q) => p.frame - q.frame);
}

function summarize(c: CompiledMotion, a: Animation, replaced: number, extendedTo: number | null, created: boolean): string {
  const objects = new Set(c.tracks.map((t) => t.objectId)).size;
  return `${created ? `Created “${a.name}” with` : `Added to “${a.name}”:`} ${c.keyCount} keyframes on ${c.tracks.length} track${c.tracks.length === 1 ? '' : 's'} for ${objects} object${objects === 1 ? '' : 's'}, frames ${c.startFrame}–${c.endFrame}${replaced ? `; replaced ${replaced} existing key${replaced === 1 ? '' : 's'}` : ''}${extendedTo ? `; extended to ${(extendedTo / a.fps).toFixed(2)} s` : ''}.`;
}
